import { FunnelPhase, TripsActionFilter } from "@/hooks/useTripsFilterHandoff";

export interface ActionCounts {
  awaitingApproval: number;
  manualProposalRequested: number;
  missingFbo: number;
  missingCrew: number;
  paidMissingPax: number;
  paidMissingFboOrCrew: number;
  paidComplete: number;
}

// A single card inside a section. Each card maps to a discrete number the
// operator can click to drill into the trips list. `getTotal` derives the
// number from raw status counts + the precomputed `actionCounts` payload.
export interface TimelineCardDef {
  key: string;
  label: string;
  getTotal: (
    counts: Record<string, number>,
    actionCounts: ActionCounts,
  ) => number;
  clickFilter: {
    funnelPhase?: FunnelPhase;
    statuses?: string[];
  };
}

// Per-section color theme for the title text. Concrete color values live
// in styles.ts; this token just tags each section so the rendering layer
// can pick the right tint. Used purely for subtle visual separation —
// the rest of each section stays neutral (white card, grey borders).
export type SectionColorTheme = "blue" | "amber" | "purple" | "green";

// A grouped column of one or more cards, headed by a section title with an
// info tooltip. Sections render horizontally on desktop with right-pointing
// chevron icons between them; on small viewports they stack vertically
// with a downward chevron between each pair.
export interface TimelineSectionDef {
  key: string;
  label: string;
  description: string;
  colorTheme: SectionColorTheme;
  cards: TimelineCardDef[];
}

const c = (counts: Record<string, number>, key: string): number =>
  counts[key] ?? 0;

// Operator-facing dashboard pipeline. Pre-customer-selection stages
// (intake / sourcing / proposals-ready / customer-reviewing) are owned by
// the Fllite admin team and intentionally hidden here — the operator's
// pipeline starts the moment the customer has picked a proposal.
export const TIMELINE_SECTIONS: TimelineSectionDef[] = [
  {
    key: "requests",
    label: "Requests",
    description:
      "Customer has selected proposal and waiting for your approval",
    colorTheme: "blue",
    cards: [
      {
        key: "approval",
        label: "Approval",
        // AI-RTP trip requested (fully operator-blocking) plus the
        // standard-flow proposal_approved BCs whose contractSignStatus ==
        // AWAITING_CHARTER_APPROVAL — i.e. the customer picked a proposal
        // and the trip request awaits the operator's approval.
        getTotal: (counts, ac) =>
          c(counts, "ai_rtp_trip_requested") + ac.awaitingApproval,
        clickFilter: { funnelPhase: "operator_approval_needed" },
      },
    ],
  },
  {
    key: "pending",
    label: "Pending",
    description: "Waiting on customer to complete an action",
    colorTheme: "amber",
    cards: [
      {
        key: "customer_signing",
        label: "Customer Signing",
        // proposal_approved minus the AWAITING_CHARTER_APPROVAL subset
        // (post-approval, the customer still has to sign), plus the AI-RTP
        // awaiting-signature state.
        getTotal: (counts, ac) =>
          c(counts, "ai_rtp_awaiting_signature") +
          Math.max(0, c(counts, "proposal_approved") - ac.awaitingApproval),
        clickFilter: { funnelPhase: "customer_signing" },
      },
      {
        key: "payment_pending",
        label: "Payment Pending",
        getTotal: (counts) => c(counts, "trip_booked"),
        clickFilter: { statuses: ["trip_booked"] },
      },
      {
        key: "payment_processing",
        label: "Payment Processing",
        getTotal: (counts) => c(counts, "awaiting_payment"),
        clickFilter: { statuses: ["awaiting_payment"] },
      },
    ],
  },
  {
    key: "paid_pending_info",
    label: "Paid & Pending Info",
    description: "Customer has booked but needs additional information",
    colorTheme: "purple",
    cards: [
      {
        key: "paid_missing_pax",
        label: "Pax Missing",
        getTotal: (_, ac) => ac.paidMissingPax,
        clickFilter: { funnelPhase: "paid_missing_pax" },
      },
      {
        key: "paid_missing_fbo_or_crew",
        label: "FBO/Crew Missing",
        getTotal: (_, ac) => ac.paidMissingFboOrCrew,
        clickFilter: { funnelPhase: "paid_missing_fbo_or_crew" },
      },
    ],
  },
  {
    key: "booked",
    label: "Booked",
    description: "All trip information complete",
    colorTheme: "green",
    cards: [
      {
        key: "paid_complete",
        label: "Done",
        getTotal: (_, ac) => ac.paidComplete,
        clickFilter: { funnelPhase: "paid_complete" },
      },
    ],
  },
];

export const getCardTotal = (
  card: TimelineCardDef,
  counts: Record<string, number> | undefined,
  actionCounts: ActionCounts,
): number => card.getTotal(counts ?? {}, actionCounts);

// Re-exported for back-compat with `useOperatorBusinessCaseStatusCounts`,
// which still imports the type from this module.
export type { TripsActionFilter };
